/**
 * Floating-window state for the source viewer: title-bar drag, 8-direction
 * edge/corner resize, maximize, viewport clamping, and localStorage bounds
 * persistence. Pure bounds math is exported separately so tests cover it
 * without simulating pointer events.
 */

import { useState, useCallback, useRef, useEffect } from 'react'

export interface WindowBounds {
  x: number
  y: number
  width: number
  height: number
}

export interface WindowViewport {
  width: number
  height: number
}

export interface MinSize {
  minWidth: number
  minHeight: number
}

export type ResizeEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'

/** clamp v into [lo, hi]; if the range is inverted (hi < lo), lo wins */
function clampNum(v: number, lo: number, hi: number): number {
  return Math.min(Math.max(v, lo), Math.max(lo, hi))
}

/** Keep bounds fully inside the viewport; minimums win over viewport when it is tiny. */
export function clampBounds(b: WindowBounds, vp: WindowViewport, min: MinSize): WindowBounds {
  const width = clampNum(b.width, min.minWidth, vp.width)
  const height = clampNum(b.height, min.minHeight, vp.height)
  const x = clampNum(b.x, 0, vp.width - width)
  const y = clampNum(b.y, 0, vp.height - height)
  return { x, y, width, height }
}

export function centeredBounds(
  vp: WindowViewport,
  size: { width: number; height: number },
  min: MinSize
): WindowBounds {
  return clampBounds(
    {
      x: Math.round((vp.width - size.width) / 2),
      y: Math.round((vp.height - size.height) / 2),
      width: size.width,
      height: size.height,
    },
    vp,
    min
  )
}

/** Drag never changes size — only position, clamped to the viewport. */
export function applyDrag(
  start: WindowBounds,
  dx: number,
  dy: number,
  vp: WindowViewport
): WindowBounds {
  return {
    ...start,
    x: clampNum(start.x + dx, 0, vp.width - start.width),
    y: clampNum(start.y + dy, 0, vp.height - start.height),
  }
}

/**
 * Resize from an edge/corner. The opposite edge stays fixed: e/w pin the
 * left/right edge respectively, n/s pin the bottom/top edge.
 */
export function applyResize(
  start: WindowBounds,
  edge: ResizeEdge,
  dx: number,
  dy: number,
  vp: WindowViewport,
  min: MinSize
): WindowBounds {
  let { x, y, width, height } = start
  if (edge.includes('e')) {
    width = clampNum(start.width + dx, min.minWidth, vp.width - start.x)
  }
  if (edge.includes('w')) {
    const right = start.x + start.width
    width = clampNum(start.width - dx, min.minWidth, right)
    x = right - width
  }
  if (edge.includes('s')) {
    height = clampNum(start.height + dy, min.minHeight, vp.height - start.y)
  }
  if (edge.includes('n')) {
    const bottom = start.y + start.height
    height = clampNum(start.height - dy, min.minHeight, bottom)
    y = bottom - height
  }
  return { x, y, width, height }
}

/** Parse persisted bounds JSON; anything invalid falls back to centered defaults. */
export function restoreBounds(
  raw: string | null,
  vp: WindowViewport,
  fallbackSize: { width: number; height: number },
  min: MinSize
): WindowBounds {
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as Partial<WindowBounds> | null
      if (
        parsed &&
        typeof parsed === 'object' &&
        [parsed.x, parsed.y, parsed.width, parsed.height].every((n) => Number.isFinite(n))
      ) {
        return clampBounds(parsed as WindowBounds, vp, min)
      }
    } catch {
      // fall through to default
    }
  }
  return centeredBounds(vp, fallbackSize, min)
}

export interface UseFloatingWindowOptions {
  defaultWidth: number
  defaultHeight: number
  minWidth?: number
  minHeight?: number
  storageKey?: string
  enabled?: boolean
}

export interface UseFloatingWindowReturn {
  bounds: WindowBounds
  isMaximized: boolean
  isDragging: boolean
  isResizing: boolean
  toggleMaximize: () => void
  resetBounds: () => void
  titleBarProps: {
    onPointerDown: (e: React.PointerEvent) => void
    onDoubleClick: () => void
  }
  getResizeHandleProps: (edge: ResizeEdge) => {
    onPointerDown: (e: React.PointerEvent) => void
  }
}

const EDGE_CURSORS: Record<ResizeEdge, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
}

interface ActiveGesture {
  mode: 'drag' | ResizeEdge
  startClientX: number
  startClientY: number
  startBounds: WindowBounds
}

function viewport(): WindowViewport {
  return { width: window.innerWidth, height: window.innerHeight }
}

export function useFloatingWindow(options: UseFloatingWindowOptions): UseFloatingWindowReturn {
  const {
    defaultWidth,
    defaultHeight,
    minWidth = 480,
    minHeight = 360,
    storageKey,
    enabled = true,
  } = options

  const [bounds, setBounds] = useState<WindowBounds>(() =>
    restoreBounds(
      storageKey && typeof window !== 'undefined' ? localStorage.getItem(storageKey) : null,
      viewport(),
      { width: defaultWidth, height: defaultHeight },
      { minWidth, minHeight }
    )
  )
  const [isMaximized, setIsMaximized] = useState(false)
  const [gesture, setGesture] = useState<ActiveGesture | null>(null)

  const boundsRef = useRef(bounds)
  boundsRef.current = bounds

  // Document-level listeners while a gesture is active (useResizablePanel pattern).
  useEffect(() => {
    if (!gesture) return
    if (!enabled) {
      setGesture(null)
      return
    }
    const min = { minWidth, minHeight }
    const onMove = (e: PointerEvent) => {
      const dx = e.clientX - gesture.startClientX
      const dy = e.clientY - gesture.startClientY
      setBounds(
        gesture.mode === 'drag'
          ? applyDrag(gesture.startBounds, dx, dy, viewport())
          : applyResize(gesture.startBounds, gesture.mode, dx, dy, viewport(), min)
      )
    }
    const onUp = () => {
      setGesture(null)
      if (storageKey) localStorage.setItem(storageKey, JSON.stringify(boundsRef.current))
    }
    // A touch-driven gesture can be cancelled by the browser (e.g. it
    // reclaims the pointer for a system gesture) instead of released —
    // clear the gesture but do NOT persist bounds (cancel != release).
    const onCancel = () => {
      setGesture(null)
    }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    document.addEventListener('pointercancel', onCancel)
    document.body.style.userSelect = 'none'
    document.body.style.cursor =
      gesture.mode === 'drag' ? 'grabbing' : EDGE_CURSORS[gesture.mode]
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
      document.removeEventListener('pointercancel', onCancel)
      document.body.style.userSelect = ''
      document.body.style.cursor = ''
    }
  }, [gesture, enabled, storageKey, minWidth, minHeight])

  // Re-clamp when the browser window resizes.
  useEffect(() => {
    if (!enabled) return
    const onResize = () =>
      setBounds((b) => clampBounds(b, viewport(), { minWidth, minHeight }))
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [enabled, minWidth, minHeight])

  const startGesture = useCallback(
    (mode: ActiveGesture['mode'], e: React.PointerEvent) => {
      if (!enabled || e.button !== 0) return
      e.preventDefault()
      setGesture({
        mode,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startBounds: boundsRef.current,
      })
    },
    [enabled]
  )

  const toggleMaximize = useCallback(() => {
    if (enabled) setIsMaximized((m) => !m)
  }, [enabled])

  const resetBounds = useCallback(() => {
    setBounds(
      centeredBounds(viewport(), { width: defaultWidth, height: defaultHeight }, {
        minWidth,
        minHeight,
      })
    )
    if (storageKey) localStorage.removeItem(storageKey)
  }, [defaultWidth, defaultHeight, minWidth, minHeight, storageKey])

  return {
    bounds,
    isMaximized,
    isDragging: gesture?.mode === 'drag',
    isResizing: gesture !== null && gesture.mode !== 'drag',
    toggleMaximize,
    resetBounds,
    titleBarProps: {
      onPointerDown: (e: React.PointerEvent) => {
        // Header actions (buttons/links) must stay clickable — never start
        // a drag from them.
        if ((e.target as HTMLElement).closest('button, a')) return
        startGesture('drag', e)
      },
      onDoubleClick: toggleMaximize,
    },
    getResizeHandleProps: (edge: ResizeEdge) => ({
      onPointerDown: (e: React.PointerEvent) => {
        e.stopPropagation()
        startGesture(edge, e)
      },
    }),
  }
}

/** Matches Tailwind's `sm` breakpoint; below it the viewer renders as a sheet. */
export function useIsSmallScreen(query = '(max-width: 639px)'): boolean {
  const [matches, setMatches] = useState(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia(query).matches
  )
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia(query)
    const onChange = () => setMatches(mql.matches)
    mql.addEventListener?.('change', onChange)
    setMatches(mql.matches)
    return () => mql.removeEventListener?.('change', onChange)
  }, [query])
  return matches
}
